// Targets → Request a building (#36): the customer names a building themselves, anywhere (not
// just their districts). Search by business name or address (Google Places through our API,
// as xenquote's solar commercial lookup), pick it, and we say at a high level whether we hold
// the data for it; if it's free, they can request it for credits like any shortlisted building.
//
// Or by postcode: every property we can prepare a pack for there, by name, to pick from. A site
// with several of our buildings (a school's blocks) asks which one, by name.
//
// Exclusivity: a building another installer holds is shown as unavailable and can't be
// requested; the order itself is refused (409) if someone takes it in the meantime.
// As everywhere: never figures or where the data comes from (CLAUDE.md).
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, BadgeCheck, CheckCircle2, MapPinOff, Search, Target, XCircle } from "lucide-react";
import { ApiError, useApi, type PlacedOrder, type PlaceSuggestion, type TargetCheck, type TargetChoice } from "../api";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { PurchaseTerms, type TermsAcceptance } from "../components/PurchaseTerms";
import "../dashboard.css";
import "../find.css";
import "../targets.css";

/** Why a property can't be offered, in plain words (never where the information comes from). */
const WHY: Record<string, string> = {
  "existing-solar": "Our checks found solar panels already on this property's roof, so it isn't offered to anyone.",
  unsuitable: "Our team found that this property's roof or site isn't suitable for solar.",
  renewables: "On-site renewable generation is already recorded for this property, so it isn't offered.",
  "too-small": "Its electricity use is too low for a worthwhile Solar Opportunity Report.",
  "no-data": "We don't hold enough information about this property to prepare a report.",
  "not-yet": "We're still adding this property's latest information. Please try again in a few days.",
};

/** A fresh search session: Google bills the typing and the pick as one session. */
const newSession = () => (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`).replace(/[^A-Za-z0-9_-]/g, "");
const plural = (n: number) => `${n.toLocaleString("en-GB")} report credit${n === 1 ? "" : "s"}`;

export function RequestBuildingPage() {
  const api = useApi();
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [checking, setChecking] = useState(false);
  const [check, setCheck] = useState<TargetCheck | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [ordering, setOrdering] = useState(false);
  // Estimated figures: the customer ticks that they understand before requesting
  const [acceptEstimate, setAcceptEstimate] = useState(false);
  // The purchase terms, ticked for this property (sent and recorded with the request)
  const [accepted, setAccepted] = useState<TermsAcceptance>(null);
  const [ordered, setOrdered] = useState<PlacedOrder | null>(null);
  // Outside their districts: the customer asks for a custom quote instead
  const [quote, setQuote] = useState<"idle" | "sending" | "sent">("idle");
  // Search by name/address (Google) or list a postcode's properties
  const [mode, setMode] = useState<"name" | "postcode">("name");
  const [pc, setPc] = useState("");
  const [looking, setLooking] = useState(false);
  // Properties to pick from: a postcode's, or a site's buildings; the one picked is highlighted
  const [choices, setChoices] = useState<{ title: string; list: TargetChoice[]; postcode?: string } | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const session = useRef(newSession());
  // The result card: scrolled into view when it appears, above the list of properties
  const resultRef = useRef<HTMLElement>(null);
  const picked = useRef(""); // the text of the suggestion picked (no new search for it)

  // Suggestions as they type (debounced; 3+ characters)
  useEffect(() => {
    const text = q.trim();
    if (text.length < 3 || text === picked.current) {
      setSuggestions([]);
      return;
    }
    const t = window.setTimeout(() => {
      api
        .searchTargets(text, session.current)
        .then((s) => {
          setSuggestions(s);
          setOpen(true);
          setActive(-1);
        })
        .catch((e: Error) => setError(e.message));
    }, 250);
    return () => window.clearTimeout(t);
  }, [api, q]);

  /** Pick a suggestion: look it up against our data. */
  const pick = async (s: PlaceSuggestion) => {
    const text = [s.main, s.secondary].filter(Boolean).join(", ");
    picked.current = text;
    setQ(text);
    setOpen(false);
    setSuggestions([]);
    setChecking(true);
    setCheck(null);
    setOrdered(null);
    setQuote("idle");
    setError(null);
    setChoices(null);
    setChosen(null);
    try {
      const c = await api.checkTarget(s.placeId, session.current);
      if (c.result.status === "choose") {
        // A site with several of our buildings: list them to pick from
        setChoices({
          title: `${c.place.name || "This site"} has more than one property. Which one?`,
          list: c.result.choices,
          postcode: c.result.postcode ?? c.place.postcode,
        });
      } else setCheck(c);
    } catch (e) {
      setError(e instanceof Error ? e.message : "We couldn't look that up. Please try again.");
    } finally {
      setChecking(false);
      session.current = newSession(); // the pick ends Google's billing session
    }
  };

  /** Pick a property from a list (a postcode's or a site's): check it by its reference. */
  const choose = async (c: TargetChoice) => {
    setChosen(c.ref);
    setChecking(true);
    setCheck(null);
    setOrdered(null);
    setQuote("idle");
    setError(null);
    try {
      setCheck(await api.checkTargetRef(c.ref, choices?.postcode));
    } catch (e) {
      setError(e instanceof Error ? e.message : "We couldn't look that up. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  /** List every property we can prepare a pack for at the postcode entered. */
  const lookUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLooking(true);
    setCheck(null);
    setOrdered(null);
    setChoices(null);
    setChosen(null);
    setError(null);
    try {
      const r = await api.lookupPostcode(pc);
      setPc(r.postcode);
      if (r.choices.length) setChoices({ title: `Properties at ${r.postcode}`, list: r.choices, postcode: r.postcode });
      else setError(`We couldn't find any properties at ${r.postcode}. Check the postcode, or search by the business name instead.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "We couldn't look that postcode up. Please try again.");
    } finally {
      setLooking(false);
    }
  };

  const askForQuote = async () => {
    if (check?.result.status !== "outside") return;
    setQuote("sending");
    setError(null);
    try {
      await api.requestTargetQuote(check.result.ref, check.place.postcode);
      setQuote("sent");
    } catch (e) {
      setQuote("idle");
      setError(e instanceof Error ? e.message : "We couldn't send that. Please try again.");
    }
  };

  const request = async () => {
    if (check?.result.status !== "available") return;
    setOrdering(true);
    setError(null);
    try {
      if (!accepted) return;
      const o = await api.orderTarget(
        check.result.ref,
        check.place.postcode,
        { ...accepted, estimateAccepted: estimated && acceptEstimate },
      );
      setOrdered(o);
      setConfirming(false);
    } catch (e) {
      setConfirming(false);
      setError(e instanceof Error ? e.message : "The request didn't go through. Please try again.");
      // Taken in the meantime: show it as unavailable
      if (e instanceof ApiError && e.status === 409) setCheck({ ...check, result: { status: "unavailable" } });
    } finally {
      setOrdering(false);
    }
  };

  const r = check?.result;

  // A new result: bring it into view (it sits above the list, which may have been scrolled down)
  useEffect(() => {
    if (check) resultRef.current?.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "nearest" });
  }, [check]);
  const balance = check?.creditBalance ?? 0;
  const cost = r?.status === "available" ? r.credits : 0;
  const estimated = r?.status === "available" && r.electricityData !== "actual";

  return (
    <div className="page">
      <div className="page-head">
        <h1>Request a property</h1>
        <p>
          Have a property in mind? Search for it by business name or address, or list the properties at a postcode. If it's in your Local Authority Districts
          and we can prepare a report for it, you can request it here.
        </p>
      </div>

      <section className="card target-search">
        {/* Two ways in: search by name or address, or list a postcode's properties */}
        <div className="target-modes" role="tablist" aria-label="How to find the property">
          {(
            [
              ["name", "Name or address"],
              ["postcode", "Postcode"],
            ] as const
          ).map(([m, label]) => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={mode === m}
              className={`target-mode${mode === m ? " is-active" : ""}`}
              onClick={() => {
                setMode(m);
                setChoices(null);
                setCheck(null);
                setError(null);
              }}
            >
              {label}
            </button>
          ))}
        </div>
        {mode === "postcode" ? (
          <form className="target-postcode" onSubmit={lookUp}>
            <label htmlFor="target-pc" className="target-label">
              Postcode
            </label>
            <div className="target-pc-row">
              <div className="target-box">
                <Search size={16} aria-hidden="true" className="target-icon" />
                <input
                  id="target-pc"
                  className="input"
                  autoComplete="postal-code"
                  placeholder="e.g. HX3 0PW"
                  value={pc}
                  onChange={(e) => setPc(e.target.value.toUpperCase())}
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={looking || pc.replace(/\s/g, "").length < 5}>
                {looking ? "Searching…" : "Search"}
              </button>
            </div>
            <p className="find-note muted">We'll list every property at that postcode. Pick one and we'll tell you whether we can prepare a pack for it.</p>
          </form>
        ) : (
          <>
            <label htmlFor="target-q" className="target-label">
              Business name or address
            </label>
            <div className="target-box">
              <Search size={16} aria-hidden="true" className="target-icon" />
              <input
                id="target-q"
                className="input"
                autoComplete="off"
                placeholder="e.g. Leeds University Union, or 12 High Street, Halifax"
                value={q}
                role="combobox"
                aria-expanded={open && suggestions.length > 0}
                aria-controls="target-suggestions"
                aria-activedescendant={active >= 0 ? `target-s-${active}` : undefined}
                onChange={(e) => {
                  setQ(e.target.value);
                  picked.current = "";
                }}
                onFocus={() => setOpen(true)}
                onBlur={() => window.setTimeout(() => setOpen(false), 150)}
                onKeyDown={(e) => {
                  if (!suggestions.length) return;
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setActive((a) => Math.min(a + 1, suggestions.length - 1));
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setActive((a) => Math.max(a - 1, 0));
                  } else if (e.key === "Enter" && active >= 0) {
                    e.preventDefault();
                    void pick(suggestions[active]);
                  } else if (e.key === "Escape") setOpen(false);
                }}
              />
              {open && suggestions.length > 0 && (
                <ul id="target-suggestions" className="target-suggestions" role="listbox">
                  {suggestions.map((s, i) => (
                    <li
                      key={s.placeId}
                      id={`target-s-${i}`}
                      role="option"
                      aria-selected={i === active}
                      className={i === active ? "is-active" : ""}
                      onMouseDown={(e) => {
                        e.preventDefault(); // keep focus; pick on click
                        void pick(s);
                      }}
                    >
                      <b>{s.main}</b> <span className="muted">{s.secondary}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <p className="find-note muted">Pick the property from the list. We'll tell you straight away whether we can prepare a pack for it.</p>
          </>
        )}
      </section>

      {error && <div className="flash flash-danger">{error}</div>}
      {checking && <div className="card target-result muted">Checking our data…</div>}

      {check && r && !ordered && (
        <section ref={resultRef} className={`card target-result is-${r.status}`} aria-live="polite">
          <div className="target-where">
            <b>{check.place.name || check.place.address}</b>
            <span className="muted">{check.place.address}</span>
          </div>

          {r.status === "available" && (
            <>
              <h2 className="building-h2">
                <CheckCircle2 size={18} aria-hidden="true" className="building-ok" /> We can prepare a pack for this property
              </h2>
              <dl className="building-facts">
                <div>
                  <dt>Type</dt>
                  <dd>{r.buildingType}</dd>
                </div>
                <div>
                  <dt>District</dt>
                  <dd>{r.district}</dd>
                </div>
                <div>
                  <dt>Indicative system</dt>
                  <dd>{r.sizeLabel}</dd>
                </div>
                <div>
                  <dt>Data</dt>
                  <dd>
                    <span className={`find-data find-data-${r.electricityData}`}>{r.electricityData === "actual" ? "Actual data" : "Estimated"}</span>
                  </dd>
                </div>
              </dl>
              {/* How accurate the pack will be, before they spend anything */}
              {r.electricityData === "actual" ? (
                <div className="target-accuracy is-actual">
                  <BadgeCheck size={18} aria-hidden="true" />
                  <div>
                    <b>High accuracy.</b> This property's report is built on its actual electricity use, so the system size, savings and payback are worked out
                    from real figures.
                    {r.olderData && " Those figures are more than 3 years old, so they may not show recent changes such as solar panels added since."}
                  </div>
                </div>
              ) : (
                <div className="target-accuracy is-estimated" role="alert">
                  <AlertTriangle size={18} aria-hidden="true" />
                  <div>
                    <b>Estimated electricity use.</b> We don't have this property's actual electricity use, so the report works from an estimate for a building of
                    its type and size. Treat the system size, savings and payback as indicative until you have its bills.
                  </div>
                </div>
              )}
              <div className="target-buy">
                <span>
                  <b>{plural(cost)}</b> <span className="muted">· your balance {plural(balance)}</span>
                </span>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={balance < cost || ordering}
                  onClick={() => {
                    setAcceptEstimate(false);
                    setConfirming(true);
                  }}
                >
                  <Target size={16} aria-hidden="true" /> Request this property
                </button>
              </div>
              {balance < cost && <p className="building-small muted">You don't have enough report credits for this report yet.</p>}
            </>
          )}
          {r.status === "yours" && (
            <p className="card-sub">
              <CheckCircle2 size={16} aria-hidden="true" className="building-ok" /> This property is already yours. Find it in{" "}
              <Link to="/properties">My properties</Link>.
            </p>
          )}
          {r.status === "unavailable" && (
            <p className="card-sub">
              <XCircle size={16} aria-hidden="true" /> <b>Another installer has exclusivity on this property</b> at the moment, so it can't be requested. If
              their exclusivity ends without renewal, you can request it then.
            </p>
          )}
          {r.status === "outside" && (
            <div className="target-accuracy is-estimated target-outside" role="alert">
              <MapPinOff size={18} aria-hidden="true" />
              <div>
                <b>Outside your districts.</b> This {r.buildingType ? r.buildingType.toLowerCase() : "property"} is in <b>{r.district}</b>, and your plan covers{" "}
                {r.yourDistricts.length ? r.yourDistricts.join(", ") : "your saved Local Authority Districts"}. Reports are licensed by district, so this one
                can't be requested with credits: we'll price it for you individually.
                <div className="target-quote">
                  {quote === "sent" ? (
                    <span className="building-ok">
                      <CheckCircle2 size={16} aria-hidden="true" /> Thanks: we've got your request and will be in touch with a quote.
                    </span>
                  ) : (
                    <button type="button" className="btn btn-primary" disabled={quote === "sending"} onClick={askForQuote}>
                      {quote === "sending" ? "Sending…" : "Ask for a custom quote"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          {(r.status === "excluded" || r.status === "not-suitable") && (
            <div className="target-accuracy is-estimated target-why" role="status">
              <XCircle size={18} aria-hidden="true" />
              <div>
                <b>We can't offer this property.</b> {WHY[r.reason ?? ""] ?? WHY.unsuitable}
              </div>
            </div>
          )}
          {r.status === "not-found" && (
            <p className="card-sub">
              <XCircle size={16} aria-hidden="true" /> We can't prepare a report for this property yet: we don't hold enough information about it, or it isn't one
              our reports cover. Try another address, or a property nearby.
            </p>
          )}
        </section>
      )}

      {ordered && (
        <div className="flash flash-success order-done">
          <span>
            Requested{ordered.orderNumber ? ` (order ${ordered.orderNumber})` : ""}. It's exclusive to you for 6 months, and we'll email you when the report is
            ready.
          </span>
          {/* Straight to the property they've just bought (named now it's theirs) */}
          <Link className="order-done-link" to={`/properties/${encodeURIComponent(ordered.orderId)}`}>
            <b>{[ordered.property?.name, ordered.property?.postcode].filter(Boolean).join(", ") || "Your new property"}</b> · Open it in My properties →
          </Link>
        </div>
      )}

      {/* The properties to choose from, AFTER the result: a long list would otherwise push the
          result off screen (Neil, 14 Oct 2026). The result slides in between the search and the list */}
      {choices && (
        <section className="card target-choices" aria-live="polite">
          <h2 className="building-h2">{choices.title}</h2>
          <ul>
            {choices.list.map((c) => (
              <li key={c.ref}>
                <button type="button" className={`target-choice${chosen === c.ref ? " is-active" : ""}`} onClick={() => void choose(c)}>
                  <span>{c.name}</span>
                  {/* Public or private, at a glance */}
                  {c.ownership && <span className={`target-badge is-${c.ownership}`}>{c.ownership === "private" ? "Private sector" : "Public sector"}</span>}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <ConfirmDialog
        open={confirming}
        title="Request this property?"
        confirmLabel={`Request for ${plural(cost)}`}
        danger={false}
        size="large"
        busy={ordering}
        confirmDisabled={!accepted || (estimated && !acceptEstimate)}
        onConfirm={request}
        onCancel={() => setConfirming(false)}
      >
        <p>
          {plural(cost)} will come off your balance ({plural(balance)} now) and the property is exclusive to your company for 6 months from today. We'll email
          you when the report is ready.
        </p>
        {confirming && (
          <PurchaseTerms
            credits={cost}
            onChange={setAccepted}
            extra={
              estimated
                ? {
                    label: "I understand this report uses estimated electricity use, not the property's actual figures.",
                    checked: acceptEstimate,
                    onChange: setAcceptEstimate,
                  }
                : undefined
            }
          />
        )}
      </ConfirmDialog>
    </div>
  );
}
